import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ActionCreator } from "actions";
import { withNamespaces } from "react-i18next";
import * as queryString from "query-string";
import { fromJS } from "immutable";
import { CommonModalHeader } from "component/common";
import { isEqual } from "lodash";
import { formatTimeRemaining } from "utils/date";
import freeRoundsBannerDefault from "assets/images/freeRoundsBannerDefault.png";
import freeRoundsDefault from "assets/images/freeRoundsDefault.png";
import { currencyFormatter } from "utils/currencyHelper";
import { CategoryListItem } from "component/listItem";
import { CommonGamesList } from "component/list";
import { TableList } from "component/list";
import gameUtils from "utils/gameUtils";
import resize2fit from "utils/resizeHelper";
import "containers/FreeRounds/styles.scss";
import RejectModal from "component/modal/Reject";

class FreeRounds extends Component {
  constructor(props) {
    super(props);
    this.titleRef = createRef();
    this.onClickPrevious = this._onClickPrevious.bind(this);
    this.onCloseClickHandler = this._onCloseClickHandler.bind(this);
    this.getGames = this._getGames.bind(this);
    this.fetchList = this._fetchList.bind(this);
    this.setLabel = this._setLabel.bind(this);
    this.setValue = this._setValue.bind(this);
    this.setSkeleton = this._setSkeleton.bind(this);
    this.state = {
      showTitle: false,
      details: null,
      continueLink: null,
      gamesLink: null,
      games: null,
      gameRoundsLink: null,
      gameRoundHistory: null,
      cancelLink: null,
      isLoading: false,
      listComplete: false,
      showRejectModal: false,
    };
  }

  _onClickPrevious() {
    this.props.history.goBack();
  }

  _onCloseClickHandler() {
    this.props.navigateTo(this.props.location.pathname);
  }

  _getGames(link) {
    gameUtils.getGames(this, link);
  }

  initObserver() {
    if (
      "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window &&
      "intersectionRatio" in window.IntersectionObserverEntry.prototype
    ) {
      let observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.titleRef.current) {
                this.setState({
                  showTitle: false,
                });
              }
            } else {
              if (this.titleRef.current) {
                this.setState({
                  showTitle: true,
                });
              }
            }
          });
        },
        {
          rootMargin: "50px 50px 50px 50px",
          threshold: 0.1,
        },
      );
      observer.observe(this.titleRef.current);
    } else {
    }
  }

  _fetchList(page) {
    if (!page) {
      this.setState({
        gameRoundHistory: null,
      });
    }

    this.setState({
      isLoading: true,
      listComplete: false,
    });

    let params = {
      page: page,
    };

    this.props
      .fetchGameRoundHistory(this.state.gameRoundsLink, params)
      .then((response) => {
        let newlist = response;

        if (this.state.gameRoundHistory) {
          newlist = newlist.setIn(
            ["items"],
            this.state.gameRoundHistory
              .get("items")
              .concat(response.getIn(["items"])),
          );
        }

        let listComplete;
        try {
          listComplete =
            newlist.get("page") === newlist.get("totalPages") ||
            newlist.get("totalPages") === 0;
        } catch (err) {}

        this.setState({
          gameRoundHistory: newlist,
          isLoading: false,
          listComplete,
        });
        this.props.setGameRoundHistory(response);
      });
  }

  fetchDetails(route) {
    this.setState({
      details: null,
      continueLink: null,
      gamesLink: null,
      gameRoundsLink: null,
      cancelLink: null,
    });
    this.props.fetchFreeRoundsDetails(this.props.route).then((resp) => {
      let continueLink;
      try {
        continueLink = resp
          .get("links")
          .find((link) => link.get("rel") === "launch.real");
      } catch (err) {}
      let gamesLink;
      try {
        gamesLink = resp
          .get("links")
          .find((link) => link.get("rel") === "rewards.games");
      } catch (err) {}
      let gameRoundsLink;
      try {
        gameRoundsLink = resp
          .get("links")
          .find((link) => link.get("rel") === "rewards.game-rounds");
      } catch (err) {}
      let cancelLink;
      try {
        cancelLink = resp
          .get("links")
          .find((link) => link.get("rel") === "cancel");
      } catch (err) {}

      this.setState({
        details: resp,
        continueLink,
        gamesLink,
        gameRoundsLink,
        cancelLink,
      });
      this.getGames(this.state.gamesLink);
      this.props.setFreeRoundsDetails(resp);
    });
  }

  componentDidMount() {
    if (this.props.route) {
      this.fetchDetails(this.props.route);
      this.initObserver();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevProps.route, this.props.route) && this.props.route) {
      this.setState({
        showTitle: false,
        details: null,
        continueLink: null,
        gamesLink: null,
        games: null,
        gameRoundsLink: null,
        gameRoundHistory: null,
        isLoading: false,
        listComplete: false,
      });
      this.fetchDetails(this.props.route);
      this.initObserver();
    }
  }

  _setLabel(id) {
    let { t } = this.props;
    try {
      switch (id) {
        case "id":
          return <div>ID</div>;
        case "createdDate":
          return <div>{t(id)}</div>;
        case "totalPayout":
          return <div className="text-right">{t(id)}</div>;
        default:
          return <div>{t(id)}</div>;
      }
    } catch {
      return id;
    }
  }

  _setValue(item) {
    switch (item.id) {
      case "totalPayout":
        return this.renderAmount(item);
      case "initiatedDate":
        return this.renderDate(item);
      default:
        return item.value;
    }
  }

  _setSkeleton(key) {
    switch (key) {
      case 3:
        return (
          <td key={key} className="text-right">
            <span className="dummy">loading</span>
          </td>
        );
      default:
        return (
          <td key={key}>
            <span className="dummy">loading</span>
          </td>
        );
    }
  }

  renderAmount(item) {
    let value = currencyFormatter(item.value, {
      currency: this.props.currency,
      decimalPlaces: this.props.decimalPlaces,
    });

    return <div className="text-right">{value}</div>;
  }

  renderDate(item) {
    const date = item.value.split(" ");
    return (
      <div className="date">
        <span>{`${date[0]} ${date[1]} `}</span>
        <span>{`${date[2]}`}</span>
      </div>
    );
  }

  openRejectModal = () => {
    this.setState({ showRejectModal: true });
  };

  closeRejectModal = () => {
    this.setState({ showRejectModal: false });
  };

  handleReject = () => {
    const id = this.state.details.get("id");
    if (!id) return;

    this.props
      .fetchFreeRoundsReject(this.state.cancelLink)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        //Hide the reward card
        const element = document.querySelector(`.${id}`);
        if (element) {
          const parentToHide = element.closest(".col.template-m");
          if (parentToHide) {
            parentToHide.classList.add("d-none");
          }
        }
        return response.json();
      })
      .then((data) => {
        this.closeRejectModal();
        this.onCloseClickHandler();
        this.fetchDetails(this.props.route);
      })
      .catch((err) => {
        console.log(err);
        this.closeRejectModal();
        this.onCloseClickHandler();
      });
  };

  renderStatus() {
    if (!this.state.details) return null;

    let { t } = this.props;
    let status = this.state.details.get("status");
    let endsIn = this.state.details.get("endsIn");

    switch (status) {
      case "COMPLETED":
      case "NEW":
      case "EXPIRED":
      case "FAILED":
        return <div className="status">{t(status.toLowerCase())}</div>;
      default:
        let endsInObj = formatTimeRemaining(endsIn);
        return (
          <div className="remaining">
            {endsInObj.d &&
              endsInObj.d === 1 &&
              t("expiresInDay", { days: endsInObj.d })}
            {endsInObj.d &&
              endsInObj.d !== 1 &&
              t("expiresInDays", { days: endsInObj.d })}
            {endsInObj.w &&
              endsInObj.w === 1 &&
              t("expiresInWeek", { weeks: endsInObj.w })}
            {endsInObj.w &&
              endsInObj.w !== 1 &&
              t("expiresInWeeks", { weeks: endsInObj.w })}
            {endsInObj.m &&
              endsInObj.m === 1 &&
              t("expiresInMonth", { months: endsInObj.m })}
            {endsInObj.m &&
              endsInObj.m !== 1 &&
              t("expiresInMonths", { months: endsInObj.m })}
          </div>
        );
    }
  }

  renderGames() {
    //let filterRoute;
    //let subFiltersRoute;
    //let subFilters;
    //let sortBy;
    const ListItem = CategoryListItem;
    let gamesState;
    let isLoading;
    let isLoadingNext;
    let hasNextPage;
    let games;
    let page;
    //let size = 8;
    //let maxCount = 10;

    try {
      gamesState = this.props.gamesState.getIn([
        this.state.gamesLink.get("rel"),
      ]);
    } catch (err) {}
    try {
      games = gamesState.get("data");
    } catch (err) {}
    try {
      page = games.get("page");
    } catch (err) {}
    try {
      isLoading = gamesState.getIn(["isLoading"]);
    } catch (err) {}
    try {
      isLoadingNext = gamesState.getIn(["isLoadingNext"]);
    } catch (err) {}
    try {
      hasNextPage = games.get("totalPages") > games.get("page");
    } catch (err) {
      hasNextPage = true;
    }

    return (
      <CommonGamesList
        route={this.state.gamesLink}
        //hasNoFilters={!filterRoute}
        itemComponent={ListItem}
        isLoading={isLoading}
        isLoadingNext={isLoadingNext}
        hasNextPage={hasNextPage}
        games={games}
        fetchGames={() => {
          if (!hasNextPage) return;

          let params = new URLSearchParams();
          params.append("page", page ? page + 1 : 1);

          return this.props.fetchGames(this.state.gamesLink, params);
        }}
        isMobile={this.props.isMobile}
        onGameItemClickHandler={(item) => {
          let gameId = item.game.get("gameId");
          let rel = item.playType;
          this.props.gameClick(gameId, rel, null).then((response) => {
            if (response.location) {
              this.props.navigateTo(response.location);
            } else if (response.params) {
              this.props.setGameAction(response.params);
            }
          });
        }}
        componentWillUnmount={() => {}}
        imageFormat={this.props.imageFormat}
        latestBroadcast={this.props.latestBroadcast}
        currency={this.props.currency}
        isOneClickPlayEnabled={this.props.isOneClickPlayEnabled}
      />
    );
  }

  renderValue() {
    if (!this.state.details) return null;
    let { t } = this.props;
    let status = this.state.details.get("status");

    switch (status) {
      case "NEW":
      case "PROMOTED":
        return (
          <tr>
            <td>{t("rewardedAmount")}:</td>
            <td>
              <span>
                {t("upToValue", {
                  amount: currencyFormatter(
                    this.state.details.get("totalBetValue"),
                    {
                      currency: this.props.currency,
                    },
                  ),
                })}
              </span>
            </td>
          </tr>
        );
      default:
        return null;
    }
  }

  render() {
    if (!this.props.route) return null;

    resize2fit(document.querySelectorAll(".bet-container"));
    let { t } = this.props;

    return (
      <div className="free-rounds-modal">
        <div className="header-absolute">
          <CommonModalHeader
            title={t("freeRounds")}
            showTitle={this.state.showTitle}
            onClickPrevious={this.onClickPrevious}
            onCloseClickHandler={this.onCloseClickHandler}
          />
        </div>
        <div className="image-holder">
          <img
            className="modal-large-banner"
            src={freeRoundsBannerDefault}
            alt="banner"
          />
          <img
            className="modal-small-banner"
            src={freeRoundsDefault}
            alt="banner"
          />
          <div className="h-100 w-100 d-flex justify-content-center align-items-center amount bet-container">
            {this.state.details && this.state.details.get("totalBetValue") && (
              <span className="text-center">
                {currencyFormatter(this.state.details.get("totalBetValue"), {
                  currency: this.props.currency,
                })}
              </span>
            )}
          </div>
        </div>

        <div className="title" ref={this.titleRef}>
          {t("freeRounds")}
        </div>

        {this.renderStatus()}

        {this.state.details && (
          <div className="details">
            <table>
              <tbody>
                {this.state.details.get("totalBetValue") && this.renderValue()}
                {this.state.details.get("numberOfRoundsSummary") && (
                  <tr>
                    <td>{t("freeRounds")}:</td>
                    <td>{this.state.details.get("numberOfRoundsSummary")}</td>
                  </tr>
                )}
                {this.state.details.get("claimedGame") && (
                  <tr>
                    <td>{t("game")}:</td>
                    <td>{this.state.details.getIn(["claimedGame", "name"])}</td>
                  </tr>
                )}
                {this.state.details.get("totalRounds") && (
                  <tr>
                    <td>{t("totalRounds")}:</td>
                    <td>{this.state.details.get("totalRounds")}</td>
                  </tr>
                )}
                {(this.state.details.get("remainingRounds") ||
                  this.state.details.get("remainingRounds") === 0) && (
                  <tr>
                    <td>{t("remaining")}:</td>
                    <td>{this.state.details.get("remainingRounds")}</td>
                  </tr>
                )}
                {this.state.details.get("totalPayout") && (
                  <tr>
                    <td>{t("totalWin")}:</td>
                    <td>
                      {currencyFormatter(
                        this.state.details.get("totalPayout"),
                        { currency: this.props.currency },
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {this.state.continueLink && (
              <div className="row nopadding">
                <button
                  className="btn btn-rnd btn-sm btn-danger"
                  onClick={() => {
                    let gameId = this.state.details.getIn([
                      "claimedGame",
                      "id",
                    ]);
                    let rel = this.state.continueLink.get("rel");
                    this.props.gameClick(gameId, rel).then((response) => {
                      if (response.location) {
                        this.props.navigateTo(response.location);
                      } else if (response.params) {
                        this.props.setGameAction(response.params);
                      }
                    });
                  }}
                >
                  {t("continuePlaying")}
                </button>
              </div>
            )}
            {this.state.cancelLink && (
              <div className="row nopadding">
                <button className="reject-btn" onClick={this.openRejectModal}>
                  {t("reject")}
                </button>
              </div>
            )}
          </div>
        )}
        {this.state.showRejectModal && (
          <RejectModal
            isOpen={this.state.showRejectModal}
            onReject={this.handleReject}
            onClose={this.closeRejectModal}
          />
        )}
        {!this.state.details && (
          <div className="details">
            <table>
              <tbody>
                <tr>
                  <td>
                    <span className="dummy">Value:</span>
                  </td>
                  <td>
                    <span className="dummy">XXXXXXXXXXX</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="dummy">Free Rounds</span>
                  </td>
                  <td>
                    <span className="dummy">XXXXX</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="dummy">Game:</span>
                  </td>
                  <td>
                    <span className="dummy">XXXXXXXXXXXXXX</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="dummy">Total Rounds:</span>
                  </td>
                  <td>
                    <span className="dummy">XXXXX</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="dummy">Remaining Rounds:</span>
                  </td>
                  <td>
                    <span className="dummy">XX</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="dummy">Total Win:</span>
                  </td>
                  <td>
                    <span className="dummy">XXXXXXXXX</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {/* ?Update the condition if Claimed and In Progress endpoint is available.*/}
        {this.state.gameRoundsLink && (
          <TableList
            list={this.state.gameRoundHistory}
            initiated={true}
            isLoading={this.state.isLoading}
            listComplete={this.state.listComplete}
            setLabel={this.setLabel}
            setValue={this.setValue}
            setSkeleton={this.setSkeleton}
            setHeaderSkeleton={this.setSkeleton}
            onReachEnd={() => {
              let nextPage;
              let page = this.state.gameRoundHistory
                ? this.state.gameRoundHistory.get("page")
                : null;
              let totalPages = this.state.gameRoundHistory
                ? this.state.gameRoundHistory.get("totalPages")
                : null;

              if (totalPages && page !== totalPages) {
                nextPage = page ? page + 1 : null;
              } else if (page && totalPages && page === totalPages) {
                this.setState({
                  listComplete: true,
                });
                return;
              }

              this.fetchList(nextPage);
            }}
          />
        )}
        {!this.state.gameRoundsLink && this.state.gamesLink === undefined && (
          <div className="text-center nodata">{t("thereIsNoData")}</div>
        )}
        {this.state.gamesLink && this.renderGames()}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let headerAuthorization = state.config.getIn(["headers", "Authorization"]);
  let qs = queryString.parse(window.location.search);
  let sessionId = state.security.get("sessionId");
  let { route } = qs;
  let routeIm;

  try {
    routeIm = fromJS(JSON.parse(decodeURIComponent(atob(route))));
  } catch (err) {}

  if (
    !sessionId ||
    !routeIm ||
    (routeIm && routeIm.get("rel") !== "rewards.details")
  )
    return {};

  return {
    headerAuthorization,
    gamesState: state.games,
    isMobile: state.window.get("isMobile"),
    route: routeIm,
    currency: state.links.get("currency"),
    imageFormat: state.window.get("imageFormat"),
    isOneClickPlayEnabled: state.links.get("isOneClickPlayEnabled"),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return bindActionCreators(ActionCreator, dispatch);
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, ownProps, {
    navigateTo: (path) => ownProps.history.push(path),
    fetchGames: (route, params) => dispatchProps.fetchGames(route, params),
    fetchFreeRoundsDetails: (route) =>
      dispatchProps.fetchFreeRoundsDetails(route),
    setFreeRoundsDetails: (payload) =>
      dispatchProps.setFreeRoundsDetails(payload),
    gameClick: (gameId, playType, nav) =>
      dispatchProps.gameClick(gameId, playType, nav),
    setGameAction: (params) => dispatchProps.setGameAction(params),
    fetchGameRoundHistory: (route, params) =>
      dispatchProps.fetchGameRoundHistory(route, params),
    setGameRoundHistory: (payload) =>
      dispatchProps.setGameRoundHistory(payload),
    fetchFreeRoundsReject: (route) =>
      dispatchProps.fetchFreeRoundsReject(route),
  });
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(withNamespaces("translation")(FreeRounds));
